import dayjs from 'dayjs'
import { z } from 'zod'

import { makeTranslationsSchema } from '~/common/schemas/translations'

// Similar to promotions/constants.py
export const GiftWithPurchaseCampaignStatus = z.enum(['upcoming', 'current', 'expired', 'deleted'])
export type GiftWithPurchaseCampaignStatus = z.infer<typeof GiftWithPurchaseCampaignStatus>

export const GiftWithPurchaseCampaign = z.object({
    id: z.number(),
    store: z.number().int(),
    name: z.string().min(1, 'Name is required'),
    start_date: z.custom<Date>((data) => dayjs(data).toDate()),
    end_date: z.custom<Date>((data) => dayjs(data).toDate()),
    status: GiftWithPurchaseCampaignStatus.nullish().readonly(),
    is_active: z.boolean(),
    can_be_combined: z.boolean(),
    applies_to_all_products: z.boolean(),
})
export type GiftWithPurchaseCampaign = z.infer<typeof GiftWithPurchaseCampaign>

export const Gift = z.object({
    id: z.number(),
    stock_item_id: z.number().optional(),
    stock_item: z
        .object({
            id: z.number(),
            name: z.string().nullish(),
            cover_image_url: z.string().nullish(),
        })
        .readonly(),
    quantity: z.coerce.number().int().positive(),
})
export type Gift = z.infer<typeof Gift>

export const Tier = z.object({
    id: z.number(),
    campaign_type: z.enum(['spend', 'items']).nullish(),
    spend_threshold: z.coerce.number().nonnegative(),
    num_items_threshold: z.coerce.number().int().nonnegative(),
    max_redemptions: z.coerce.number().positive(),
    num_redeemed: z.coerce.number().nullish(),
    limit_per_customer: z.coerce.number().positive(),
    gifts: z.array(Gift.partial({ id: true })).min(1, 'At least one gift is required'),
})
export type Tier = z.infer<typeof Tier>

export const GiftWithPurchaseCampaignDetail = GiftWithPurchaseCampaign.partial({ id: true }).extend(
    {
        shop_pks: z.array(z.number().int()),
        tiers: z.array(Tier.partial({ id: true })),
        gift_with_purchase_campaign_products: z.array(
            z.object({
                is_active: z.boolean().optional(),
                product: z.object({
                    id: z.number().int(),
                    cover_image_url: z.string().nullish(),
                    translations: makeTranslationsSchema(
                        z.object({ name: z.string().nullish() })
                    ).readonly(),
                }),
            })
        ),
    }
)

export type GiftWithPurchaseCampaignDetail = z.infer<typeof GiftWithPurchaseCampaignDetail>

export const GiftWithPurchaseCampaignDetailInput = GiftWithPurchaseCampaignDetail.extend({
    shop_pks: z.array(z.object({ id: z.number().int() })),
}).transform((data, { addIssue }) => {
    if (!data.applies_to_all_products && !data.gift_with_purchase_campaign_products.length) {
        addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one product is required',
            path: ['gift_with_purchase_campaign_products'],
        })

        return z.NEVER
    }

    for (const [index, tier] of data.tiers.entries()) {
        if (tier.campaign_type === 'spend') {
            if (tier.spend_threshold <= 0) {
                addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Spend threshold is required',
                    path: ['tiers', index, 'spend_threshold'],
                })

                return z.NEVER
            }

            // reset num items to 0
            tier.num_items_threshold = 0
        } else if (tier.campaign_type === 'items') {
            if (tier.num_items_threshold <= 0) {
                addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Items threshold is required',
                    path: ['tiers', index, 'num_items_threshold'],
                })

                return z.NEVER
            }

            // reset spend to 0
            tier.spend_threshold = 0
        }
    }

    return {
        ...data,
        shop_pks: data.shop_pks.map((shop_pk) => shop_pk.id),
    } satisfies GiftWithPurchaseCampaignDetail
})
export type GiftWithPurchaseCampaignDetailInput = z.input<
    typeof GiftWithPurchaseCampaignDetailInput
>
